import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Spinner, Alert, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const FileUpload = ({ fileType, onDrop, onValidationError }) => {
    const [isScanning, setIsScanning] = useState(false);
    const [scanResult, setScanResult] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    // File size limit: 25MB
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB in bytes

    // Allowed file types based on content type
    const getAllowedFileTypes = () => {
        switch (fileType) {
            case 'image':
                return {
                    'image/jpeg': ['.jpg', '.jpeg'],
                    'image/png': ['.png'],
                };
            case 'audio':
                return {
                    'audio/wav': ['.wav'],
                    'audio/mpeg': ['.mp3'],
                };
            case 'video':
                return {
                    'video/mp4': ['.mov', '.mp4'],
                };
            case 'pdf':
                return {
                    'application/pdf': ['.pdf']
                };
            default:
                return {};
        }
    };

    const acceptType = getAllowedFileTypes();

    // File validation function
    const validateFile = (file) => {
        setErrorMessage('');
        
        // Check file size
        if (file.size > MAX_FILE_SIZE) {
            const errorMsg = `File size must be 25MB or smaller. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`;
            setErrorMessage(errorMsg);
            onValidationError && onValidationError(errorMsg);
            return false;
        }

        // Check file type
        const allowedTypes = Object.keys(acceptType);
        const fileType = file.type;
        
        if (!allowedTypes.includes(fileType)) {
            const allowedExtensions = Object.values(acceptType).flat().join(', ');
            const errorMsg = `Invalid file type. Allowed types: ${allowedExtensions}`;
            setErrorMessage(errorMsg);
            onValidationError && onValidationError(errorMsg);
            return false;
        }

        return true;
    };

    // Simulate malware scanning (in a real app, this would call your backend API)
    const scanForMalware = async (file) => {
        setIsScanning(true);
        setScanResult(null);
        
        try {
            // Simulate API call to malware scanning service
            await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate 2-second scan
            
            // In a real implementation, you would call your backend API here:
            // const formData = new FormData();
            // formData.append('file', file);
            // const response = await fetch('/api/scan-malware', {
            //     method: 'POST',
            //     body: formData
            // });
            // const result = await response.json();
            
            // For demo purposes, we'll simulate a successful scan
            const result = { isClean: true, scanId: Date.now() };
            
            if (result.isClean) {
                setScanResult({ status: 'success', message: 'File scanned successfully - No threats detected' });
                return true;
            } else {
                setScanResult({ status: 'danger', message: 'Malware detected - File rejected' });
                onValidationError && onValidationError('Malware detected in file');
                return false;
            }
        } catch (error) {
            setScanResult({ status: 'warning', message: 'Scan failed - Please try again' });
            onValidationError && onValidationError('Malware scan failed');
            return false;
        } finally {
            setIsScanning(false);
        }
    };

    const handleFileDrop = async (acceptedFiles) => {
        if (acceptedFiles.length === 0) return;
        
        const file = acceptedFiles[0];
        
        // Validate file
        if (!validateFile(file)) {
            return;
        }

        // Scan for malware
        const isClean = await scanForMalware(file);
        if (!isClean) {
            return;
        }

        // If validation and scan pass, call the original onDrop
        onDrop(acceptedFiles);
    };

    const {
        getRootProps,
        getInputProps,
        isDragActive
    } = useDropzone({
        accept: acceptType,
        onDrop: handleFileDrop,
        multiple: false,
        maxSize: MAX_FILE_SIZE
    });

    const getFileTypeDescription = () => {
        switch (fileType) {
            case 'image':
                return '.png, .jpg';
            case 'audio':
                return '.wav, .mp3';
            case 'video':
                return '.mov, .mp4';
            case 'pdf':
                return '.pdf';
            default:
                return 'supported files';
        }
    };

    return (
        <div>
            <div {...getRootProps()} style={{
                ...dropzoneStyles,
                borderColor: isDragActive ? '#007bff' : '#cccccc',
                backgroundColor: isDragActive ? '#f8f9fa' : 'white'
            }}>
                <input {...getInputProps()} />
                {isScanning ? (
                    <div style={{ textAlign: 'center' }}>
                        <Spinner animation="border" size="sm" style={{ marginRight: '8px' }} />
                        <span>Scanning file for malware...</span>
                    </div>
                ) : (
                    <div>
                        <p>Drag 'n' drop a file here, or click to select</p>
                        <em>Accepted formats: {getFileTypeDescription()} (Max 25MB)</em>
                        <br />
                        <small style={{ color: '#6c757d' }}>
                            <FontAwesomeIcon icon="fa-solid fa-shield-halved" style={{ marginRight: '4px' }} />
                            Files are automatically scanned for malware
                        </small>
                    </div>
                )}
            </div>

            {errorMessage && (
                <Alert variant="danger" style={{ marginTop: '10px', fontSize: '0.875rem' }}>
                    <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" style={{ marginRight: '8px' }} />
                    {errorMessage}
                </Alert>
            )}

            {scanResult && (
                <Alert variant={scanResult.status} style={{ marginTop: '10px', fontSize: '0.875rem' }}>
                    <FontAwesomeIcon 
                        icon={scanResult.status === 'success' ? 'fa-solid fa-check-circle' : 'fa-solid fa-exclamation-triangle'} 
                        style={{ marginRight: '8px' }} 
                    />
                    {scanResult.message}
                </Alert>
            )}
        </div>
    );
}

const dropzoneStyles = {
    border: '2px dashed #cccccc',
    borderRadius: '4px',
    padding: '20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
};

export default FileUpload;
